import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { SETTING_TABLE_NAME } from "../../constants/settingsTableName";
import {
  useSystemConfig,
  useUIState,
  useSolutionType,
  useSettingsUI,
  useLatestAlert,
  useGeneralSettingsJson,
  useSystemId,
  useAlarmCounts,
  useServiceMode,
} from "../../hooks/useReduxSelectors";
import { useMsal } from "Auth/msal-context";
import { sendCommand, setCommandButtonDisabled } from "../../ducks/uiDuck";
import { fetchSettingsAuth } from "../../ducks/settingsDuck";
import { toggleEdgeUnitInfoPanel } from "../../ducks/settingsUIDuck";
import { selectEdgePin, selectPincodeEnabled, selectPinUnlocked, setPincodeEnabled, setPinUnlocked } from "../../ducks/authenticationDuck";
import { pincodeApi } from "../../actions/apiActions";
import ToastEventLog from "./eventLog/ToastEventLog";
import LocalPasswordDialog from "./LocalPasswordDialog";
import {
  AppBar,
  Toolbar,
  Box,
  Typography,
  IconButton,
  Chip,
  Badge,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Snackbar,
  Alert,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import RefreshIcon from "@mui/icons-material/Refresh";
import NotificationsIcon from "@mui/icons-material/Notifications";
import BuildIcon from "@mui/icons-material/Build";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import EventNoteIcon from "@mui/icons-material/EventNote";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

const DashboardTopBar = ({
  currentMenuLabel = "Overview",
  dashboardCount,
  isCompact,
  onToggleLeftDrawer,
  onToggleRightDrawer,
}) => {
  const theme = useTheme();
  const systemId = useSystemId();
  const ui = useUIState();
  const config = useSystemConfig();
  const solutionType = useSolutionType();
  const showEdgeUnitStatusPanel = useSettingsUI()?.showEdgeUnitStatusPanel;
  const latestToastEvent = useLatestAlert();
  const generalSettingsJson = useGeneralSettingsJson();
  const alarmCounts = useAlarmCounts();
  const { i18n, t } = useTranslation("common");
  const dispatch = useDispatch();
  const { isAuthorized, isEdge } = useMsal();
  const edgePin = useSelector(selectEdgePin);
  const pincodeEnabled = useSelector(selectPincodeEnabled);
  const pinUnlocked = useSelector(selectPinUnlocked);

  const serviceModeActive = ui?.serviceMode?.isActive;
  const isServiceModeDisabled =
    ui?.serviceMode?.isServiceModeBtnDisabled || (!isEdge && !isAuthorized);
  const serviceMode = useServiceMode();
  const totalAlarms =
    (alarmCounts?.devices || 0) +
    (alarmCounts?.samples || 0) +
    (alarmCounts?.warnings || 0);

  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinErrorSnackbar, setPinErrorSnackbar] = useState(false);

  // --- Check pincode status on edge ---
  useEffect(() => {
    if (isEdge && config?.SETTING_URL) {
      pincodeApi.getStatus(config.SETTING_URL)
        .then((status) => {
          dispatch(setPincodeEnabled(!!status?.pincodeEnabled));
        })
        .catch(() => {
          dispatch(setPincodeEnabled(false));
        });
    }
  }, [isEdge, config?.SETTING_URL, dispatch]);

  // --- Settings fetch ---
  useEffect(() => {
    if (config && solutionType !== "lora") {
      dispatch(
        fetchSettingsAuth({
          systemId,
          args: {
            setting: SETTING_TABLE_NAME.GENERAL_SETTING,
            stateName: "GeneralSettings",
          },
        })
      );
    }
  }, [dispatch, config, i18n]);

  useEffect(() => {
    if (generalSettingsJson) {
      i18n.changeLanguage(generalSettingsJson.language);
    }
  }, [generalSettingsJson, i18n]);

  // --- Refresh button (long-press to clear cache) ---
  const [counter, setCounter] = useState(0);
  const intervalRef = useRef(null);

  useEffect(() => {
    return () => stopCounterCleanup();
  }, []);

  const startCounter = () => {
    if (intervalRef.current) return;
    intervalRef.current = setInterval(() => {
      setCounter((prev) => prev + 1);
    }, 500);
  };

  const stopCounter = () => {
    if (counter > 6) {
      if (window.confirm("Clear browser local storage?")) {
        localStorage.clear();
        window.location.reload();
      }
    } else {
      window.location.reload();
    }
    stopCounterCleanup();
  };

  const stopCounterCleanup = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      setCounter(0);
      intervalRef.current = null;
    }
  };

  // --- Events popover ---
  const [toastLogAnchorEl, setToastLogAnchorEl] = useState(null);
  const openToastMsgLog = Boolean(toastLogAnchorEl);

  const handleToggleToastMsgLog = (event) => {
    setToastLogAnchorEl(openToastMsgLog ? null : event.currentTarget);
  };

  // --- Service mode toggle ---
  const dispatchServiceMode = (value) => {
    dispatch(
      sendCommand({
        argsProps: {
          HwId: "EdgeDevice",
          Command: "ServiceMode",
          Value: value,
          customerId: config?.CUSTOMER_ID || "",
          deviceId: config?.DEVICE_ID || "",
        },
        commandType: "serviceMode",
        systemId,
        group: "group",
      })
    );
    dispatch(
      setCommandButtonDisabled({
        data: { isServiceModeBtnDisabled: true, commandType: "serviceMode" },
        systemId,
      })
    );
  };

  const handleServiceMode = async () => {
    // Deactivation never requires PIN
    if (serviceModeActive) {
      dispatchServiceMode("Off");
      return;
    }

    dispatchServiceMode("On");
  };

  const isServiceModeLocked = isEdge && pincodeEnabled && !pinUnlocked && !serviceModeActive;

  const handleLockToggle = async () => {
    if (pinUnlocked) {
      // Re-lock
      dispatch(setPinUnlocked(false));
      return;
    }

    if (edgePin) {
      try {
        await pincodeApi.validate(config.SETTING_URL, edgePin);
        dispatch(setPinUnlocked(true));
        return;
      } catch {
        setPinErrorSnackbar(true);
      }
    }
    // No stored PIN or stored PIN rejected — show dialog
    setPinDialogOpen(true);
  };

  // --- Auto/Manual mode toggle ---
  const handleAutoManualToggle = () => {
    const isManual = serviceMode?.status === "ManualActive";
    dispatch(
      sendCommand({
        argsProps: {
          HwId: "EdgeDevice",
          customerId: config?.CUSTOMER_ID || "",
          deviceId: config?.DEVICE_ID || "",
          Command: "ManualMode",
          Value: isManual ? "Off" : "On",
        },
        commandType: isManual ? "Manual" : "Auto",
        systemId,
      })
    );
  };

  const isManualMode = serviceMode?.status === "ManualActive";

  return (
    <>
      <AppBar
        position="static"
        elevation={0}
        sx={{
          backgroundColor: serviceModeActive ? "warning.dark" : "inherit",
          borderBottom: "2px solid",
          borderColor: serviceModeActive ? "warning.main" : theme.palette.surface.mutedBorder,
        }}
      >
        <Toolbar
          variant="dense"
          sx={{
            minHeight: 64,
            gap: 1,
            "&.MuiToolbar-root": {
              px: "0px",
            }
          }}
        >
          {/* Navigation drawer toggle (compact / mobile) */}
          {isCompact && (
            <IconButton
              color="inherit"
              edge="start"
              onClick={onToggleLeftDrawer}
              sx={{
                borderRadius: 0,
                width: "52px",
                height: "64px",
                m: 0,
                p: 0,
                borderRight: "2px solid",
                borderColor: theme.palette.surface.mutedBorder
              }}
            >
              <MenuIcon fontSize={isCompact ? "medium" : "large"} />
            </IconButton>
          )}

          {/* Refresh (hold to clear cache) */}
          {/* Remove from compact view */}
          {!isCompact &&
            <Tooltip title={t("Refresh")}>
              <IconButton
                color="inherit"
                onMouseDown={startCounter}
                onMouseUp={stopCounter}
                onMouseLeave={stopCounterCleanup}
                onTouchStart={startCounter}
                onTouchEnd={stopCounter}
                onTouchCancel={stopCounterCleanup}
                sx={{ borderRadius: 0, width: "64px", height: "64px", m: 0, p: 0, borderRight: "2px solid", borderColor: theme.palette.surface.mutedBorder }}
              >
                <RefreshIcon fontSize={isCompact ? "medium" : "large"} />
              </IconButton>
            </Tooltip>
          }

          {/* Status panel toggle (all screen sizes, non-lora) */}
          {solutionType !== "lora" && (
            <Tooltip title={t("Toggle status panel")}>
              {isCompact ? (
                <IconButton
                  color="inherit"

                  onClick={() => dispatch(toggleEdgeUnitInfoPanel({ systemId }))}
                >
                  <InfoOutlinedIcon fontSize={isCompact ? "medium" : "large"} />
                </IconButton>
              ) : (
                <Chip
                  label={showEdgeUnitStatusPanel ? t("Status") + " \u25B2" : t("Status") + " \u25BC"}

                  variant={showEdgeUnitStatusPanel ? "filled" : "outlined"}
                  onClick={() => dispatch(toggleEdgeUnitInfoPanel({ systemId }))}
                  sx={{
                    color: showEdgeUnitStatusPanel ? "inherit" : "inherit",
                    borderColor: theme.palette.topBar.borderMuted,
                    ml: 0.5,
                    p: 1,
                  }}
                />
              )}
            </Tooltip>
          )}

          {/* Current view label (centered) */}
          <Box sx={{ flex: 1, textAlign: "center", minWidth: 0, display: "flex", flexDirection: "column" }}>
            <Typography>
              {t(currentMenuLabel)}
            </Typography>
            {serviceModeActive && !isCompact && (
              <Typography
                variant="caption"
                sx={{ color: "inherit", opacity: 0.85, lineHeight: 1 }}
              >
                {t("SERVICE MODE")}
              </Typography>
            )}
          </Box>

          {/* Events area (desktop — clickable event text, non-lora) */}
          {!isCompact && solutionType !== "lora" && (
            <Tooltip title={t("Events")}>
              <Chip
                onClick={handleToggleToastMsgLog}
                sx={{ color: "inherit", borderColor: theme.palette.topBar.borderMuted }}
                label={"Events"}
              />
              {/* <Typography
                variant="caption"
                noWrap
                sx={{ color: "inherit", fontSize: "0.7rem", display: "block" }}
              >
                {latestToastEvent?.Reason || t("No events")}
              </Typography>
              {latestToastEvent?.timeStamp && (
                <Typography
                  variant="caption"
                  sx={{ color: "inherit", opacity: 0.6, fontSize: "0.6rem" }}
                >
                  {latestToastEvent.timeStamp}
                </Typography>
              )} */}
            </Tooltip>
          )}

          {/* Events icon button (mobile, non-lora) */}
          {isCompact && solutionType !== "lora" && (
            <Tooltip title={t("Events")}>
              <IconButton color="inherit" onClick={handleToggleToastMsgLog}>
                <EventNoteIcon fontSize={isCompact ? "medium" : "large"} />
              </IconButton>
            </Tooltip>
          )}

          <ToastEventLog
            open={openToastMsgLog}
            toastLogAnchorEl={toastLogAnchorEl}
            onClose={() => setToastLogAnchorEl(null)}
            dashboardCount={dashboardCount}
          />

          {/* Alarm bell (compact / mobile) */}
          {isCompact && (
            <IconButton color="inherit" onClick={onToggleRightDrawer}>
              <Badge badgeContent={totalAlarms} color="error" max={99}>
                <NotificationsIcon fontSize={isCompact ? "medium" : "large"} />
              </Badge>
            </IconButton>
          )}

          {/* Service mode toggle + Auto/Manual (non-lora) */}
          {solutionType !== "lora" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0 }}>
              {/* Lock/Unlock button (only on edge with pincode enabled) */}
              {isEdge && pincodeEnabled && !serviceModeActive && (
                <Tooltip
                  title={
                    pinUnlocked
                      ? t("Lock Service Mode")
                      : t("Unlock Service Mode")
                  }
                >
                  <IconButton
                    color="inherit"
                    onClick={handleLockToggle}
                    sx={{
                      borderRadius: 0,
                      width: isCompact ? "42px" : "48px",
                      height: "64px",
                      m: 0,
                      p: 0,
                      borderLeft: "2px solid",
                      borderColor: theme.palette.surface.mutedBorder,
                    }}
                  >
                    {pinUnlocked
                      ? <LockOpenIcon fontSize={isCompact ? "medium" : "large"} />
                      : <LockIcon fontSize={isCompact ? "medium" : "large"} />
                    }
                  </IconButton>
                </Tooltip>
              )}

              <Tooltip
                title={
                  serviceModeActive
                    ? t("Deactivate Service Mode")
                    : t("Activate Service Mode")
                }
              >
                <IconButton
                  color="inherit"
                  onClick={handleServiceMode}
                  disabled={isServiceModeDisabled || isServiceModeLocked}

                  sx={{
                    bgcolor: serviceModeActive
                      ? theme.palette.topBar.hoverBg
                      : "transparent",
                    "&:hover": { bgcolor: theme.palette.warning.main },
                    borderRadius: 0,
                    width: isCompact ? "52px" : "64px",
                    height: "64px",
                    m: 0,
                    p: 0,
                    borderLeft: "2px solid",
                    borderColor: theme.palette.surface.mutedBorder
                  }}

                >
                  <BuildIcon fontSize={isCompact ? "medium" : "large"} />
                </IconButton>
              </Tooltip>


            </Box>
          )}
        </Toolbar>

        {/* Service mode banner (compact only) */}
        {serviceModeActive && isCompact && (
          <Box
            sx={{
              textAlign: "center",
              py: 0.25,
              bgcolor: theme.palette.topBar.activeBg,
            }}
          >
            <Typography variant="caption" sx={{ fontWeight: 600, letterSpacing: 1 }}>
              {t("SERVICE MODE")}
            </Typography>
            {/* Auto / Manual toggle — appears when service mode is active (compact/mobile only, desktop uses alarm sidebar) */}
            {isCompact && serviceModeActive && (
              <ToggleButtonGroup
                value={isManualMode ? "manual" : "auto"}
                exclusive
                sx={{
                  height: 28,
                  pl: 2,
                  "& .MuiToggleButton-root": {
                    fontSize: "0.7rem",
                    fontWeight: 600,
                    color: theme.palette.topBar.textMuted,
                    borderColor: theme.palette.topBar.borderFaint,
                    "&.Mui-selected": { color: "common.white" },
                  },
                }}
              >
                <ToggleButton
                  value="auto"
                  onClick={handleAutoManualToggle}
                  sx={{ "&.Mui-selected": { bgcolor: "success.main", "&:hover": { bgcolor: "success.dark" } } }}
                >
                  {t("Auto")}
                </ToggleButton>
                <ToggleButton
                  value="manual"
                  onClick={handleAutoManualToggle}
                  sx={{ "&.Mui-selected": { bgcolor: "warning.main", "&:hover": { bgcolor: "warning.dark" } } }}
                >
                  {t("Manual")}
                </ToggleButton>
              </ToggleButtonGroup>
            )}
          </Box>
        )
        }
      </AppBar >

      {isEdge && (
        <LocalPasswordDialog
          open={pinDialogOpen}
          onClose={() => setPinDialogOpen(false)}
          onSuccess={() => dispatch(setPinUnlocked(true))}
          baseUrl={config?.SETTING_URL ?? ""}
        />
      )
      }
      <Snackbar
        open={pinErrorSnackbar}
        autoHideDuration={4000}
        onClose={() => setPinErrorSnackbar(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity="error" onClose={() => setPinErrorSnackbar(false)}>
          {t("Incorrect PIN")}
        </Alert>
      </Snackbar>
    </>
  );
};

export default DashboardTopBar;
